import { useEffect, useState } from 'react';
import { apiFetch } from '../../../api';

const INP = {
  width: '100%', padding: '10px 13px', borderRadius: 5,
  background: 'rgba(255,255,255,.05)', border: '1px solid rgba(201,168,76,.18)',
  color: '#fff', fontSize: '.84rem', fontFamily: 'var(--sans)',
  outline: 'none', boxSizing: 'border-box',
};
const LBL = {
  fontSize: '.6rem', color: 'rgba(255,255,255,.36)',
  letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5, display: 'block',
};
const ERR = { color: '#f87171', fontSize: '.7rem', marginTop: 3 };

const STATUSES = [
  { value: 'OFF_PLAN',           label: 'Off-Plan' },
  { value: 'UNDER_CONSTRUCTION', label: 'Under Construction' },
  { value: 'READY_TO_MOVE',      label: 'Ready to Move' },
];

function slugify(text) {
  return text.toLowerCase().trim()
    .replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-').replace(/^-+|-+$/g, '');
}

export default function Step1BasicInfo({ form, update, errors }) {
  const [developers, setDevelopers] = useState([]);
  const [loadingDevs, setLoadingDevs] = useState(true);
  const [newDevName, setNewDevName] = useState('');
  const [addingDev, setAddingDev] = useState(false);
  const [showDevInput, setShowDevInput] = useState(false);

  useEffect(() => {
    apiFetch('/api/dev-projects/developers/all')
      .then(setDevelopers)
      .catch(() => setDevelopers([]))
      .finally(() => setLoadingDevs(false));
  }, []);

  const createDeveloper = async () => {
    if (!newDevName.trim()) return;
    setAddingDev(true);
    try {
      const dev = await apiFetch('/api/dev-projects/developers', {
        method: 'POST',
        body: JSON.stringify({ name: newDevName.trim() }),
      });
      setDevelopers(prev => [...prev, dev].sort((a, b) => a.name.localeCompare(b.name)));
      update({ developerId: dev.id });
      setNewDevName('');
      setShowDevInput(false);
    } catch {
      // silently ignore — user can retry
    } finally {
      setAddingDev(false);
    }
  };

  const sectionHead = {
    color: 'rgba(255,255,255,.85)', fontSize: '.82rem', fontWeight: 700,
    marginBottom: '1rem', marginTop: '.5rem',
    paddingBottom: '.5rem', borderBottom: '1px solid rgba(201,168,76,.1)',
    display: 'flex', alignItems: 'center', gap: 7,
  };

  return (
    <div>
      <div style={sectionHead}>
        <span style={{ color: 'var(--gold)' }}>①</span> Basic Information
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.85rem 1rem', marginBottom: '1rem' }}>
        {/* Project Name */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={LBL}>Project Name *</label>
          <input
            id="step1-project-name"
            name="name"
            style={{ ...INP, borderColor: errors.name ? 'rgba(248,113,113,.5)' : 'rgba(201,168,76,.18)' }}
            value={form.name}
            onChange={e => {
              update({ name: e.target.value, slug: slugify(e.target.value) });
            }}
            placeholder="e.g. Azure Crown Residences"
          />
          {errors.name && <div style={ERR}>{errors.name}</div>}
          {form.name && (
            <div style={{ fontSize: '.68rem', color: 'rgba(255,255,255,.28)', marginTop: 3 }}>
              Slug preview: <span style={{ color: 'rgba(201,168,76,.6)' }}>{slugify(form.name)}</span>
            </div>
          )}
        </div>

        {/* Developer */}
        <div>
          <label style={LBL}>Developer *</label>
          {loadingDevs ? (
            <div style={{ ...INP, color: 'rgba(255,255,255,.3)' }}>Loading…</div>
          ) : (
            <select
              id="step1-developer-select"
              name="developerId"
              style={{ ...INP, cursor: 'pointer', appearance: 'none',
                borderColor: errors.developerId ? 'rgba(248,113,113,.5)' : 'rgba(201,168,76,.18)' }}
              value={form.developerId}
              onChange={e => update({ developerId: e.target.value })}
            >
              <option value="">— Select Developer —</option>
              {developers.map(d => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          )}
          {errors.developerId && <div style={ERR}>{errors.developerId}</div>}
          <div
            onClick={() => setShowDevInput(v => !v)}
            style={{ fontSize: '.7rem', color: 'var(--gold)', marginTop: 5, cursor: 'pointer', userSelect: 'none' }}
          >
            + Add new developer
          </div>
          {showDevInput && (
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <input
                id="step1-new-developer-name"
                name="newDeveloperName"
                style={{ ...INP, flex: 1 }}
                value={newDevName}
                onChange={e => setNewDevName(e.target.value)}
                placeholder="Developer name"
                onKeyDown={e => e.key === 'Enter' && createDeveloper()}
              />
              <button
                onClick={createDeveloper}
                disabled={addingDev}
                style={{
                  background: 'var(--gold)', border: 'none', borderRadius: 5,
                  padding: '8px 14px', color: '#000', fontWeight: 700,
                  fontSize: '.78rem', cursor: 'pointer', fontFamily: 'var(--sans)',
                }}
              >
                {addingDev ? '…' : 'Add'}
              </button>
            </div>
          )}
        </div>

        {/* Status */}
        <div>
          <label style={LBL}>Project Status</label>
          <select
            id="step1-project-status"
            name="status"
            style={{ ...INP, cursor: 'pointer', appearance: 'none' }}
            value={form.status}
            onChange={e => update({ status: e.target.value })}
          >
            {STATUSES.map(s => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Description */}
      <div>
        <label style={LBL}>Project Description</label>
        <textarea
          id="step1-project-description"
          name="description"
          style={{
            ...INP, height: 110, resize: 'vertical', lineHeight: 1.6,
          }}
          value={form.description}
          onChange={e => update({ description: e.target.value })}
          placeholder="Describe the project — location highlights, vision, lifestyle, etc."
        />
      </div>
    </div>
  );
}
